import { Shipment } from '../../../../generated/graphql'
import {
  getPlannedDeliveryDate,
  getRebookingDate,
  MilestoneColumn,
  MilestoneIcon,
} from './ShipmentMilestones'
import { get } from '../../../../common/components/properties/Properties'
import { formatShortDate, milestoneDate } from '../../../../common/utils/formatter'

function calculateDeliveryIcon({
  calculatedShipmentStatusCode,
  actualDeliveryDate,
}: Pick<Shipment, 'calculatedShipmentStatusCode' | 'actualDeliveryDate'>) {
  if (calculatedShipmentStatusCode === 'PARTIALLY_DELIVERED') {
    return MilestoneIcon.Partial
  }
  return actualDeliveryDate ? MilestoneIcon.Completed : MilestoneIcon.Planned
}

export enum MilestoneHeader {
  Planned = 'Planned',
  Shipped = 'Shipped',
  Delivered = 'Delivered',
  FirstPossibleDelivery = 'First Possible Delivery',
  LastPossibleDelivery = 'Last Possible Delivery',
  Cancelled = 'Cancelled',
}

export const milestoneConfig: readonly MilestoneColumn[] = [
  {
    header: MilestoneHeader.Planned,
    getIconName: ({ actualShipmentPlannedDate }) =>
      actualShipmentPlannedDate ? MilestoneIcon.Completed : MilestoneIcon.Planned,
    getActualDate: (s: Shipment) =>
      milestoneDate(get(s, 'actualShipmentPlannedDate'), { showTime: false }),
    isGreyedOut: (s: Shipment) => (s.shipmentCancelTimestamp ? true : false),
  },
  {
    header: MilestoneHeader.Shipped,
    getIconName: ({ actualShippedTimestamp, shipmentStatusCode }) =>
      actualShippedTimestamp
        ? shipmentStatusCode === 'CANCELLED'
          ? MilestoneIcon.Planned
          : MilestoneIcon.Completed
        : MilestoneIcon.Planned,
    planned: 'plannedShipmentReadyTimestamp',
    getPlannedDate: (s: Shipment) => milestoneDate(get(s, 'plannedShipmentReadyTimestamp')),
    getActualDate: (s: Shipment) =>
      s.shipmentCancelTimestamp ? undefined : milestoneDate(get(s, 'actualShippedTimestamp')),
    isGreyedOut: (s: Shipment) => (s.shipmentCancelTimestamp ? true : false),
  },
  {
    header: MilestoneHeader.FirstPossibleDelivery,
    getIconName: () => MilestoneIcon.Planned,
    getPlannedDate: (s: Shipment) =>
      s.deliveries[0]?.fulfillmentRequestDate?.estimatedDeliveryTimestamp
        ? formatShortDate(s.deliveries[0].fulfillmentRequestDate.estimatedDeliveryTimestamp)
        : undefined,
    isColumnVisible: (s: Shipment) =>
      !!s.deliveries[0]?.fulfillmentRequestDate?.estimatedDeliveryTimestamp &&
      s.iDPIndicator === false &&
      s.deliveries[0].nikeSalesOrderTypeCode !== 'Z033' &&
      s.deliveries[0].nikeSalesOrderTypeCode !== 'ZDIG' &&
      //hardcoded APL carriers
      s.shipmentVendors[0].vendorCode + s.shipmentVendors[0].hubCode !== 'UPSOH450' &&
      s.shipmentVendors[0].vendorCode + s.shipmentVendors[0].hubCode !== 'UPSEH550',
    planned: 'estimatedDeliveryTimestamp',
    isGreyedOut: (s: Shipment) => (s.shipmentCancelTimestamp ? true : false),
  },
  {
    header: MilestoneHeader.Delivered,
    getIconName: (shipment) => calculateDeliveryIcon(shipment),
    planned: 'estimatedDeliveryDate',
    getPlannedDate: (shipment: Shipment) =>
      getPlannedDeliveryDate({ shipment, dateFormat: milestoneDate }),
    getActualDate: (s: Shipment) => milestoneDate(get(s, 'actualDeliveryDate')),
    getRebookingDate: (s: Shipment) => getRebookingDate({ shipment: s }),
    isGreyedOut: (s: Shipment) => (s.shipmentCancelTimestamp ? true : false),
  },
  {
    header: MilestoneHeader.LastPossibleDelivery,
    getIconName: () => MilestoneIcon.Planned,
    getPlannedDate: (s: Shipment) =>
      s.deliveries[0]?.fulfillmentRequestDate?.cancelByDt
        ? milestoneDate(s.deliveries[0].fulfillmentRequestDate.cancelByDt)
        : undefined,
    isColumnVisible: (s: Shipment) =>
      !!s.deliveries[0]?.fulfillmentRequestDate?.cancelByDt &&
      s.iDPIndicator === false &&
      s.deliveries[0].nikeSalesOrderTypeCode !== 'Z033' &&
      s.deliveries[0].nikeSalesOrderTypeCode !== 'ZDIG' &&
      //hardcoded APL carriers
      s.shipmentVendors[0].vendorCode + s.shipmentVendors[0].hubCode !== 'UPSOH450' &&
      s.shipmentVendors[0].vendorCode + s.shipmentVendors[0].hubCode !== 'UPSEH550',
    planned: 'cancelByDt',
    isGreyedOut: (s: Shipment) => (s.shipmentCancelTimestamp ? true : false),
  },
  {
    header: MilestoneHeader.Cancelled,
    getIconName: () => MilestoneIcon.Cancelled,
    isColumnVisible: (s: Shipment) => !!s.shipmentCancelTimestamp,
    getActualDate: (s: Shipment) => milestoneDate(get(s, 'shipmentCancelTimestamp')),
    isGreyedOut: (s: Shipment) => (s.shipmentCancelTimestamp ? false : true),
  },
]
